import React, { useState, useEffect } from "react";
import {
  Title,
  Table,
  Button,
  Group,
  Modal,
  TextInput,
  Textarea,
  ActionIcon,
  Loader,
  Center,
  Text,
  Box,
  Divider,
  ScrollArea,
  ThemeIcon,
  Badge,
  Alert,
  Paper,
} from "@mantine/core";
import { DateInput } from '@mantine/dates';
import { useDisclosure } from "@mantine/hooks";
import { 
  IconPlus, 
  IconTrash, 
  IconPencil, 
  IconLeaf, 
  IconCalendar,
  IconSoup,
  IconInfoCircle,
  IconRefresh
} from "@tabler/icons-react";

import api from "../../services/api";

export default function MenuPenyediaPage() {
  // --- State Data ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // --- State Modal ---
  const [opened, { open, close }] = useDisclosure(false);
  const [editingId, setEditingId] = useState(null);

  // --- State Form ---
  const [namaMenu, setNamaMenu] = useState("");
  const [deskripsi, setDeskripsi] = useState("");
  const [tanggalMenu, setTanggalMenu] = useState(new Date());
  
  // Default 1 baris bahan kosong
  const [bahanList, setBahanList] = useState([{ nama_bahan: "", jumlah: "", satuan: "" }]);

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/penyedia/menu");
      setData(res.data.data || []);
    } catch (error) {
      console.error("Gagal load menu:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 2. HANDLERS ---

  const handleEdit = (item) => {
    setEditingId(item.id);
    setNamaMenu(item.nama_menu);
    setDeskripsi(item.deskripsi);
    setTanggalMenu(new Date(item.tanggal_menu));
    
    if (item.bahan_makanans?.length > 0) {
        setBahanList(item.bahan_makanans.map(b => ({
            nama_bahan: b.nama_bahan, jumlah: b.jumlah, satuan: b.satuan
        })));
    } else {
        setBahanList([{ nama_bahan: "", jumlah: "", satuan: "" }]);
    }
    open();
  };

  const handleSubmit = async () => {
    if (!namaMenu || !deskripsi || !tanggalMenu) {
      alert("Mohon lengkapi Nama, Deskripsi, dan Tanggal.");
      return;
    }
    
    const validBahan = bahanList.filter(b => b.nama_bahan);
    if (validBahan.length === 0) {
      alert("Mohon isi minimal satu bahan makanan.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        nama_menu: namaMenu,
        deskripsi: deskripsi,
        tanggal_menu: tanggalMenu.toLocaleDateString('en-CA'),
        bahan: validBahan
      };

      if (editingId) {
        await api.put(`/penyedia/menu/${editingId}`, payload);
      } else {
        await api.post("/penyedia/menu", payload);
      }
      
      handleModalClose();
      fetchData();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Gagal menyimpan menu.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setEditingId(null);
    setNamaMenu("");
    setDeskripsi("");
    setTanggalMenu(new Date());
    setBahanList([{ nama_bahan: "", jumlah: "", satuan: "" }]);
    close();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin hapus menu ini?")) return;
    try {
      await api.delete(`/penyedia/menu/${id}`);
      fetchData();
    } catch (error) {
      alert("Gagal hapus menu.");
    }
  };

  const addBahanRow = () => setBahanList([...bahanList, { nama_bahan: "", jumlah: "", satuan: "" }]);
  const removeBahanRow = (i) => {
    const l = [...bahanList]; l.splice(i, 1); setBahanList(l);
  };
  const handleBahanChange = (i, f, v) => {
    const l = [...bahanList]; l[i][f] = v; setBahanList(l);
  };

  // --- 3. RENDER UI ---

  if (loading) return <Center h={300}><Loader size="lg" /></Center>;

  const rows = data.map((item) => (
    <Table.Tr key={item.id}>
      
      {/* Kolom Tanggal */}
      <Table.Td>
         <Text fz="sm" fw={500}>
             {new Date(item.tanggal_menu).toLocaleDateString('id-ID', {
                 weekday: 'short', day: 'numeric', month: 'short'
             })}
         </Text>
         <Text fz="xs" c="dimmed">
             {new Date(item.tanggal_menu).getFullYear()}
         </Text>
      </Table.Td>

      {/* Kolom Nama Menu & Deskripsi */}
      <Table.Td>
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <ThemeIcon size={36} radius="xl" variant="light" color="orange" mt={2}>
             <IconSoup size={20} />
          </ThemeIcon>
          <div>
            <Text fz="sm" fw={600} mb={2}>{item.nama_menu}</Text>
            <Text fz="xs" c="dimmed" lineClamp={2} style={{ maxWidth: 250, lineHeight: 1.4 }}>
                {item.deskripsi}
            </Text>
          </div>
        </Group>
      </Table.Td>
      
      {/* Kolom Komposisi (Horizontal Tags) */}
      <Table.Td>
        {item.bahan_makanans && item.bahan_makanans.length > 0 ? (
            <Group gap={6}>
                {item.bahan_makanans.map((b, idx) => (
                    <Badge 
                        key={idx} 
                        size="sm" 
                        variant="outline" 
                        color="gray" 
                        style={{ textTransform: 'none', fontWeight: 500, paddingLeft: 8, paddingRight: 8 }}
                    >
                        {b.nama_bahan} <span style={{ opacity: 0.5, marginLeft: 4 }}>({b.jumlah} {b.satuan})</span>
                    </Badge>
                ))}
            </Group>
        ) : (
            <Text fz="xs" c="dimmed" fs="italic">- Tidak ada data bahan -</Text>
        )}
      </Table.Td>
      
      {/* Kolom Aksi */}
      <Table.Td>
        <Group gap={4} justify="flex-end">
            <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(item)}>
                <IconPencil size={18} />
            </ActionIcon>
            
            <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(item.id)}>
                <IconTrash size={18} />
            </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div style={{ padding: 20 }}> 
      
      <Group justify="space-between" mb="lg">
        <div>
            <Title order={2}>Kelola Menu Makanan</Title>
            <Text c="dimmed" size="sm">Atur jadwal dan komposisi menu harian</Text>
        </div>
        <Group>
            <Button variant="default" leftSection={<IconRefresh size={16}/>} onClick={fetchData}>
                Refresh
            </Button>
            <Button onClick={open} leftSection={<IconPlus size={16}/>}>
                Tambah Menu
            </Button>
        </Group>
      </Group>

      {data.length === 0 && !loading && (
         <Alert icon={<IconInfoCircle size={16}/>} color="blue" mb="md" title="Informasi" variant="light">
            Belum ada menu yang dibuat. Silakan klik tombol "Tambah Menu" untuk memulai.
         </Alert>
      )}

      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
                <Table.Thead bg="gray.0">
                    <Table.Tr>
                        <Table.Th style={{ width: 140 }}>Tanggal</Table.Th>
                        <Table.Th style={{ width: 300 }}>Detail Menu</Table.Th>
                        <Table.Th>Komposisi Bahan</Table.Th>
                        <Table.Th style={{ width: 100 }} ta="right">Aksi</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length > 0 ? rows : (
                        <Table.Tr>
                            <Table.Td colSpan={4} align="center" c="dimmed" py="xl">
                                Data menu kosong.
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </ScrollArea>
      </Paper>

      {/* --- MODAL FORM --- */}
      <Modal 
        opened={opened} 
        onClose={handleModalClose} 
        title={<Text fw={700}>{editingId ? "Edit Menu" : "Buat Menu Baru"}</Text>}
        size="lg" 
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        <Box>
            <DateInput
                label="Tanggal Menu"
                value={tanggalMenu}
                onChange={setTanggalMenu}
                mb="md"
                leftSection={<IconCalendar size={16}/>}
                withAsterisk
                placeholder="Pilih tanggal penyajian"
            />

            <TextInput
                label="Nama Menu"
                placeholder="Contoh: Paket Nasi Ayam Teriyaki"
                required
                mb="sm"
                value={namaMenu}
                onChange={(e) => setNamaMenu(e.target.value)}
            />

            <Textarea
                label="Deskripsi"
                placeholder="Jelaskan detail rasa, tekstur, atau catatan khusus..."
                mb="lg"
                minRows={2}
                value={deskripsi}
                onChange={(e) => setDeskripsi(e.target.value)}
                required
            />

            <Divider 
                my="md" 
                label={<Text size="xs" fw={700} c="dimmed" tt="uppercase">Komposisi Bahan</Text>} 
                labelPosition="center" 
            />
            
            <Box bg="gray.0" p="xs" style={{ borderRadius: 8 }}>
                {bahanList.map((bahan, index) => (
                    <Group key={index} mb="sm" align="flex-end" gap="xs">
                        <TextInput 
                            placeholder="Nama Bahan (ex: Beras)" 
                            value={bahan.nama_bahan}
                            onChange={(e) => handleBahanChange(index, 'nama_bahan', e.target.value)}
                            style={{ flex: 3 }}
                            size="xs"
                        />
                        <TextInput 
                            placeholder="Jml" 
                            value={bahan.jumlah}
                            onChange={(e) => handleBahanChange(index, 'jumlah', e.target.value)}
                            style={{ flex: 1 }}
                            size="xs"
                        />
                        <TextInput 
                            placeholder="Satuan" 
                            value={bahan.satuan}
                            onChange={(e) => handleBahanChange(index, 'satuan', e.target.value)}
                            style={{ flex: 1 }}
                            size="xs"
                        />
                        
                        <ActionIcon 
                            color="red" 
                            variant="transparent" 
                            onClick={() => removeBahanRow(index)}
                            disabled={bahanList.length === 1} 
                            mb={2}
                        >
                            <IconTrash size={16}/>
                        </ActionIcon>
                    </Group>
                ))}

                <Button 
                    variant="subtle" 
                    size="xs" 
                    fullWidth 
                    mt="xs" 
                    onClick={addBahanRow} 
                    leftSection={<IconPlus size={14}/>}
                >
                    Tambah Baris Bahan
                </Button>
            </Box>

            <Group justify="flex-end" mt="xl">
                <Button variant="default" onClick={handleModalClose} disabled={submitting}>Batal</Button>
                <Button onClick={handleSubmit} loading={submitting}>Simpan Menu</Button>
            </Group>
        </Box>
      </Modal>

    </div>
  );
}